import KeyValueGrid from "./KeyValueGrid";
import { mapFieldsToValues } from "../../../utils/utils";
import FileUploadCard from "./FileUploadCard";
import SectionCard from "./SectionCard";
import {
  installmentsTypes,
  schemeTypes,
} from "../../../config/appliedSchemesConfig";
import {
  capitalSubsidySecondInstallmentDocuments,
  insertAssistanceDocuments,
} from "../../../config/incentiveClaimFormUtils";

const ApplicationSectionRenderer = ({
  section,
  formData,
  setFormData,
  incentiveClaimId = null,
  installmentType = null,
  forPdf = false,
  language = false,
}) => {
  if (!section) return null;

  const fileFields = section.fields?.filter(
    (f) =>
      formData[f.name] &&
      typeof formData[f.name] === "object" &&
      ("url" in formData[f.name] || "id" in formData[f.name]),
  );
  const normalFields = section.fields?.filter(
    (f) => !fileFields?.some((ff) => ff.name === f.name),
  );

  return (
    <div className="bg-white rounded p-3">
      <SectionCard
        title={language ? section.title.hi : section.title.en}
        color="blue"
        padding={forPdf ? "pb-6" : ""}
      >
        {/* normal fields */}
        {normalFields?.length > 0 && (
          <KeyValueGrid
            data={mapFieldsToValues(normalFields, formData, language)}
            incentiveClaimId={incentiveClaimId}
            language={language}
          />
        )}

        {/* file fields */}
        {fileFields?.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
            {fileFields.map((f) => {
              const file = formData[f.name];
              const labelName = language
                ? f.label?.hi || (file.label || f.name).replace(/_/g, " ")
                : (file.label || f.name).replace(/_/g, " ");
              return (
                <FileUploadCard
                  key={f.name}
                  label={labelName}
                  fileUrl={file.url}
                  fileName={file.name}
                  isEdit={false}
                  onFileChange={
                    setFormData
                      ? (newFile) =>
                          setFormData((prev) => ({
                            ...prev,
                            [f.name]: { ...file, fileObj: newFile },
                          }))
                      : undefined
                  }
                  padding={forPdf ? "pt-1 pb-4" : ""}
                />
              );
            })}
          </div>
        )}

        {/* sub-sections */}
        {section.subSections?.map((sub, subIndex) => {
          const subFileFields = sub.fields?.filter(
            (f) =>
              formData[f.name] &&
              typeof formData[f.name] === "object" &&
              ("url" in formData[f.name] || "id" in formData[f.name]),
          );

          const subNormalFields = sub.fields?.filter(
            (f) => !subFileFields?.some((ff) => ff.name === f.name),
          );

          // 🟡 Skip Stamp Duty sections for Interest Assistance scheme
          if (
            sub.title.en.includes("Stamp Duty") &&
            String(incentiveClaimId) === schemeTypes.interestassistance.value
          ) {
            return null;
          }

          if (
            sub.title.en.includes("Stamp Duty Documents") ||
            (sub.title.en.includes("Total Stamp Duty") &&
              (formData["total_stamp_duty_per_year"] === 0 ||
                !formData["total_stamp_duty_per_year"]))
          ) {
            return null;
          }

          if (
            sub.title.en.includes("Stamp Duty") &&
            (formData["total_stamp_duty_per_year"] === 0 ||
              !formData["total_stamp_duty_per_year"])
          ) {
            return (
              <SectionCard
                title={language ? sub.title.hi : sub.title.en}
                color="blue"
              >
                <div>{language ? "योग्य नहीं" : "Not Eligible"}</div>
              </SectionCard>
            );
          }

          if (
            sub.title.en.includes("Tax Documents") &&
            (installmentType === installmentsTypes.first.value ||
              String(incentiveClaimId) === schemeTypes.interestassistance.value)
          ) {
            return null;
          }

          if (
            (sub.title.en.includes(
              "Bank Details Section for Interest Subsidy Second Installment",
            ) ||
              sub.title.en.includes(
                "Bank Details Section for Interest Subsidy Third Installment",
              )) &&
            String(incentiveClaimId) === schemeTypes.interestassistance.value &&
            installmentType === installmentsTypes.first.value
          ) {
            return null;
          }

          if (
            sub.title.en.includes(
              "Bank Details Section for Interest Subsidy Third Installment",
            ) &&
            String(incentiveClaimId) === schemeTypes.interestassistance.value &&
            installmentType === installmentsTypes.second.value
          ) {
            return null;
          }

          let visibleFields = [...(sub.fields || [])];

          visibleFields = visibleFields.filter(
            (f) =>
              !(
                capitalSubsidySecondInstallmentDocuments.includes(f.name) &&
                String(incentiveClaimId) !==
                  schemeTypes.sreimbursementcapitalincentive.value
              ),
          );

          visibleFields = visibleFields.filter(
            (f) =>
              !(
                insertAssistanceDocuments.includes(f.name) &&
                String(incentiveClaimId) !==
                  schemeTypes.interestassistance.value
              ),
          );

          const fileFields = visibleFields.filter(
            (f) =>
              formData[f.name] &&
              typeof formData[f.name] === "object" &&
              ("url" in formData[f.name] || "id" in formData[f.name]),
          );

          const normalFields = visibleFields.filter(
            (f) => !fileFields.some((ff) => ff.name === f.name),
          );

          return (
            <div
              key={subIndex}
              className="mt-6 border border-gray-200 rounded p-4 bg-gray-50 page-break-avoid"
            >
              {sub.title && (
                <h4 className="font-bold text-theme-color mb-3 text-xl">
                  {language ? sub.title.hi : sub.title.en}
                </h4>
              )}

              {normalFields?.length > 0 && (
                <KeyValueGrid
                  data={mapFieldsToValues(normalFields, formData, language)}
                  incentiveClaimId={incentiveClaimId}
                  language={language}
                />
              )}

              {fileFields?.length > 0 && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
                  {fileFields.map((f) => {
                    const file = formData[f.name];
                    const labelName = language
                      ? f.label?.hi || (file.label || f.name).replace(/_/g, " ")
                      : (file.label || f.name).replace(/_/g, " ");
                    return (
                      <FileUploadCard
                        key={f.name}
                        label={labelName}
                        fileUrl={file.url}
                        fileName={file.name}
                        padding={forPdf ? "pt-1 pb-4" : ""}
                      />
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </SectionCard>
    </div>
  );
};

export default ApplicationSectionRenderer;
